import { SAVED_VIEW_NAME_MAX } from '@ofmos/shared/view';
import { useEffect, useState } from 'react';
import { Button, Dialog, FormField, Input } from '../../../ui';

/**
 * NAMING A VIEW - the one dialog behind both "Save as a new view" and "Rename".
 *
 * They are the same question ("what is this list called?") with the same constraint (the
 * name is unique per object, enforced by a partial unique index), so they are one
 * component with two titles rather than two components that drift apart on the copy.
 *
 * THE DUPLICATE-NAME REFUSAL IS RENDERED, NOT PREVENTED. Checking the loaded list before
 * submitting would be a client-side guess about a shared, concurrently-edited set; the
 * server's 409 is the answer, and it arrives with a sentence the dialog shows verbatim.
 */
export interface ViewNameDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly mode: 'create' | 'rename';
  readonly initialName: string;
  readonly onSubmit: (name: string) => void;
  readonly isSaving: boolean;
  /** The server's message, verbatim. Cleared by the caller when the dialog reopens. */
  readonly error: string | null;
}

export function ViewNameDialog({
  open,
  onOpenChange,
  mode,
  initialName,
  onSubmit,
  isSaving,
  error,
}: ViewNameDialogProps) {
  const [name, setName] = useState(initialName);

  useEffect(() => {
    // Seeded on the OPEN edge: a rename starts from the current name, a save-as starts
    // from whatever the caller suggested, and neither should inherit the other's text.
    if (open) setName(initialName);
  }, [open, initialName]);

  const trimmed = name.trim();

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={mode === 'create' ? 'Save view' : 'Rename view'}
      description={
        mode === 'create'
          ? 'Everyone in this instance sees the same views, so give it a name the team will recognize.'
          : 'The new name replaces the old one everywhere, including for anybody who has this view open.'
      }
      width="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={isSaving}
            disabled={trimmed === ''}
            onClick={() => onSubmit(trimmed)}
          >
            {mode === 'create' ? 'Save' : 'Rename'}
          </Button>
        </div>
      }
    >
      <FormField
        label="Name"
        {...(error === null ? {} : { error })}
        hint="Filters, sort, columns and table-or-board are saved with it."
      >
        {(props) => (
          <Input
            {...props}
            value={name}
            maxLength={SAVED_VIEW_NAME_MAX}
            invalid={error !== null}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && trimmed !== '') {
                event.preventDefault();
                onSubmit(trimmed);
              }
            }}
            autoFocus
          />
        )}
      </FormField>
    </Dialog>
  );
}
